import {
  viewerFrameAnimationMs,
  viewerZoomAnimationMs,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/preferInstantViewerMotion'
import {
  CONTEXT_FRAMING,
  framedRegionScale,
  type RegionFraming,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import type { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useCallback } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

/**
 * Ceiling for programmatic zoom (region framing and the zoom-in button's
 * "at max" state): scale 3 ≈ 300% of the rendered page.
 */
export const MAX_SCALE = 3

export const FRAME_VERIFY_BUFFER_MS = 50

const FRAME_SCALE_TOLERANCE = 0.02
const MAX_FRAME_REAPPLIES = 2
const FRAME_RETRY_MS = 250
/** useFitToViewport re-applies a virgin wrapper (scale 1 at the origin) at this delay. */
const VIRGIN_REFIT_MS = 400

export type FrameTransform = { posX: number; posY: number; scale: number }

const FRAME_EASING = 'easeInOutQuart'

/**
 * Applies a frame, then snaps it back with no animation if a later fit
 * overwrote it. One check follows the zoom animation; another follows the
 * 400ms virgin re-fit, which can land after the first check.
 */
export function holdFramedRegion(
  apply: (animationTime: number) => FrameTransform | null,
  readScale: () => number | undefined,
  hooks?: {
    onApplied?: () => void
    onSettled?: () => void
    expectedScale?: () => number | undefined
  }
): () => void {
  const animationMs = viewerFrameAnimationMs()
  const applied = apply(animationMs)
  if (!applied) return () => {}
  hooks?.onApplied?.()

  const timers: ReturnType<typeof setTimeout>[] = []
  let reapplies = 0

  const snapIfOverwritten = () => {
    const scale = readScale()
    if (scale === undefined) return
    const expected = hooks?.expectedScale?.() ?? applied.scale
    const held = Math.abs(scale - expected) <= FRAME_SCALE_TOLERANCE
    if (held || reapplies >= MAX_FRAME_REAPPLIES) return
    reapplies += 1
    apply(0)
  }

  const firstCheckMs = animationMs + FRAME_VERIFY_BUFFER_MS
  const afterVirginRefitMs = Math.max(
    firstCheckMs + FRAME_RETRY_MS,
    VIRGIN_REFIT_MS + FRAME_VERIFY_BUFFER_MS
  )
  timers.push(setTimeout(snapIfOverwritten, firstCheckMs))
  timers.push(
    setTimeout(() => {
      snapIfOverwritten()
      if (readScale() !== undefined) hooks?.onSettled?.()
    }, afterVirginRefitMs)
  )
  return () => timers.forEach(clearTimeout)
}

const FRAME_POSITION_TOLERANCE_PX = 1

function isStillFramed(
  pinch: ReactZoomPanPinchRef,
  frame: FrameTransform | null
): boolean {
  if (!frame) return false
  if (pinch.instance.animation) return true
  const { scale, positionX, positionY } = pinch.instance.transformState
  return (
    Math.abs(scale - frame.scale) <= FRAME_SCALE_TOLERANCE &&
    Math.abs(positionX - frame.posX) <= FRAME_POSITION_TOLERANCE_PX &&
    Math.abs(positionY - frame.posY) <= FRAME_POSITION_TOLERANCE_PX
  )
}

export function followFrameOnResize(
  pinch: ReactZoomPanPinchRef | null,
  lastFrame: () => FrameTransform | null,
  apply: (animationTime: number) => FrameTransform | null
): () => void {
  const wrapper = pinch?.instance.wrapperComponent
  if (!pinch || !wrapper || typeof ResizeObserver === 'undefined') {
    return () => {}
  }
  let size = { w: wrapper.clientWidth, h: wrapper.clientHeight }
  const observer = new ResizeObserver(() => {
    const next = { w: wrapper.clientWidth, h: wrapper.clientHeight }
    const resized =
      Math.abs(next.w - size.w) >= FRAME_POSITION_TOLERANCE_PX ||
      Math.abs(next.h - size.h) >= FRAME_POSITION_TOLERANCE_PX
    if (!resized) return
    size = next
    if (isStillFramed(pinch, lastFrame())) apply(viewerFrameAnimationMs())
  })
  observer.observe(wrapper)
  return () => observer.disconnect()
}

export function frameRegionInViewer(
  pinch: ReactZoomPanPinchRef | null,
  bbox: NormalizedBBox,
  minScale: number | undefined,
  animationTime: number,
  framing: RegionFraming = CONTEXT_FRAMING
): FrameTransform | null {
  if (!pinch) return null
  const wrapper = pinch.instance.wrapperComponent
  const contentComponent = pinch.instance.contentComponent
  if (!wrapper || !contentComponent) return null

  const wrapRect = wrapper.getBoundingClientRect()
  const contentW = contentComponent.offsetWidth
  const contentH = contentComponent.offsetHeight
  if (wrapRect.width === 0 || contentW === 0) return null

  const scale = Math.min(
    MAX_SCALE,
    Math.max(
      minScale ?? 0.05,
      framedRegionScale(
        bbox,
        { w: contentW, h: contentH },
        { w: wrapRect.width, h: wrapRect.height },
        framing
      )
    )
  )
  const posX =
    wrapRect.width / 2 - (bbox.left + bbox.width / 2) * contentW * scale
  const posY =
    wrapRect.height / 2 - (bbox.top + bbox.height / 2) * contentH * scale
  pinch.setTransform(posX, posY, scale, animationTime, FRAME_EASING)
  return { posX, posY, scale }
}

export const useZoomControls = (
  boxes: Box[],
  imgNatural: { w: number; h: number } | null,
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  resetToFit?: () => void,
  onResetCallback?: () => void,
  minScale?: number
) => {
  const panToCenter = useCallback(
    (imgCenterX: number, imgCenterY: number, scale: number) => {
      if (!transformRef.current || !imgNatural?.w) return

      const wrapper = transformRef.current.instance.wrapperComponent
      const contentComponent = transformRef.current.instance.contentComponent
      if (!wrapper || !contentComponent) return

      const wrapRect = wrapper.getBoundingClientRect()
      // Offset dimensions are the layout size at scale 1: unlike a client
      // rect divided by transformState.scale, they stay valid when a fit or
      // pan animation is mid-flight and the rect and state disagree.
      const scaleX = contentComponent.offsetWidth / imgNatural.w
      const scaleY = contentComponent.offsetHeight / imgNatural.h

      const posX = wrapRect.width / 2 - imgCenterX * scaleX * scale
      const posY = wrapRect.height / 2 - imgCenterY * scaleY * scale

      transformRef.current.setTransform(
        posX,
        posY,
        scale,
        viewerZoomAnimationMs()
      )
    },
    [imgNatural, transformRef]
  )

  const zoomToBox = useCallback(
    (id: string, zoomLevel = 1.5) => {
      const box = boxes.find((b) => b.id === id)
      if (!box) return
      panToCenter(box.x + box.w / 2, box.y + box.h / 2, zoomLevel)
    },
    [boxes, panToCenter]
  )

  const panToRegion = useCallback(
    (bbox: NormalizedBBox) => {
      if (!imgNatural || !transformRef.current) return
      const scale = transformRef.current.instance.transformState.scale
      panToCenter(
        (bbox.left + bbox.width / 2) * imgNatural.w,
        (bbox.top + bbox.height / 2) * imgNatural.h,
        scale
      )
    },
    [imgNatural, transformRef, panToCenter]
  )

  const frameRegion = useCallback(
    (
      bbox: NormalizedBBox,
      animationTime = viewerFrameAnimationMs(),
      framing?: RegionFraming
    ): FrameTransform | null => {
      if (!imgNatural?.w) return null
      return frameRegionInViewer(
        transformRef.current,
        bbox,
        minScale,
        animationTime,
        framing
      )
    },
    [imgNatural, transformRef, minScale]
  )

  const resetZoom = useCallback(() => {
    if (resetToFit) {
      resetToFit()
    } else if (transformRef.current) {
      transformRef.current.resetTransform(viewerZoomAnimationMs())
    }
    onResetCallback?.()
  }, [resetToFit, transformRef, onResetCallback])

  return { zoomToBox, panToRegion, frameRegion, resetZoom }
}
